import { z } from 'zod';
import { Id } from '../primitives.js';

/**
 * Контракты зубной формулы и паспорта зуба.
 *
 * Главное правило модуля выражено прямо здесь, в схеме: номер зуба проверяется
 * перечислением. Классическая и дорогая ошибка стоматологического софта —
 * запись диагноза не тому зубу из-за путаницы систем нумерации; допустить сюда
 * «зуб 19» значит однажды удалить не тот.
 *
 * Документация модуля: docs/03-modules/03-dental-chart.md
 */

/** Постоянный прикус: квадранты 1–4, зубы 1–8. */
const PERMANENT = [1, 2, 3, 4].flatMap((q) => [1, 2, 3, 4, 5, 6, 7, 8].map((n) => q * 10 + n));
/** Молочный прикус: квадранты 5–8, зубы 1–5. */
const PRIMARY = [5, 6, 7, 8].flatMap((q) => [1, 2, 3, 4, 5].map((n) => q * 10 + n));

export const FDI_CODES: readonly number[] = [...PERMANENT, ...PRIMARY];

export const Fdi = z
  .number()
  .int()
  .refine((code) => FDI_CODES.includes(code), {
    message: 'Такого зуба нет: номер должен быть по FDI (11–48 или 51–85)',
  });

export const Surface = z.enum(['M', 'D', 'V', 'L', 'O', 'C']);
export type Surface = z.infer<typeof Surface>;

export const ToothEventKind = z.enum([
  'diagnosis',
  'procedure',
  'restoration',
  'extraction',
  'note',
]);
export type ToothEventKind = z.infer<typeof ToothEventKind>;

/**
 * Уточнение внутри вида. Оно определяет, что нарисует формула, поэтому список
 * закрытый: свободная строка означала бы, что состояние зуба зависит от того,
 * как врач набрал слово.
 */
export const ToothEventDetail = z.enum([
  'caries',
  'pulpitis',
  'periodontitis',
  'impacted',
  'filling',
  'endodontic',
  'cleaning',
  'crown',
  'implant',
  'bridge_abutment',
  'bridge_pontic',
  'veneer',
  'inlay',
  'observation',
  'other',
]);
export type ToothEventDetail = z.infer<typeof ToothEventDetail>;

export const ToothState = z.enum([
  'healthy',
  'observation',
  'caries',
  'pulpitis',
  'periodontitis',
  'filled',
  'treated_endo',
  'crown',
  'bridge_abutment',
  'bridge_pontic',
  'implant',
  'impacted',
  'absent',
]);
export type ToothState = z.infer<typeof ToothState>;

/**
 * Запись в паспорте зуба. Неизменяема: исправление — новая запись со ссылкой
 * `supersedesId` на исходную (ADR-0008). Поэтому здесь нет ни `rowVersion`, ни
 * `updatedAt`: править нечего.
 *
 * Привязки к наряду лаборатории, услуге прайса и гарантии появятся вместе с
 * этими модулями; сейчас их нет, чтобы контракт не описывал то, чего не бывает.
 */
export const ToothEvent = z.object({
  id: Id,
  tenantId: Id,
  patientId: Id,
  fdi: Fdi,
  kind: ToothEventKind,
  detail: ToothEventDetail.nullable(),
  surfaces: z.array(Surface).max(6).default([]),
  /** Код МКБ-10: K02.1 и подобные. Только для диагнозов. */
  icd10Code: z.string().max(10).nullable(),
  notes: z.string().max(2000).nullable(),
  /** Кто это сделал: врач или ассистент. */
  performedBy: Id,
  /**
   * Дата вмешательства — та, что стоит в медицинской карте. Может быть в
   * прошлом: коронку, поставленную три года назад в другой клинике, вносят
   * сегодня, но датируют тем годом.
   */
  performedAt: z.iso.datetime(),
  /**
   * Когда врач внёс запись на устройстве. Отличается от `createdAt`, если
   * писали без сети: приём не должен выглядеть задокументированным задним
   * числом. Расхождение с серверным временем больше суток показывается врачу.
   */
  clientCreatedAt: z.iso.datetime(),
  supersedesId: Id.nullable(),
  /** Почему исправили. Заполнено только у исправляющей записи. */
  correctionReason: z.string().max(500).nullable(),
  /**
   * Гарантия конструкции, поставленной этой записью. Заполняется только
   * переходом наряда лаборатории в `installed` — в форме внесения записи
   * этого поля нет, гарантию не назначают руками.
   */
  warrantyMonths: z.number().int().min(0).max(600).nullable(),
  warrantyUntil: z.iso.date().nullable(),
  createdAt: z.iso.datetime(),
});
export type ToothEvent = z.infer<typeof ToothEvent>;

/**
 * Запись вместе с обратной ссылкой на ту, что её исправила.
 *
 * Отдельная схема, а не поле в `ToothEvent`, потому что в базе этого поля нет и
 * быть не может: append-only таблица не позволяет дописать ссылку в уже
 * внесённую строку. Обратная связь выводится при чтении — и сервером, и
 * клиентом из кэша. Держи её `ToothEvent` — и любая строка из кэша перестала бы
 * проходить собственную схему.
 */
export const ToothEventView = ToothEvent.extend({
  supersededById: Id.nullable(),
});
export type ToothEventView = z.infer<typeof ToothEventView>;

/**
 * Внесение записи.
 *
 * Момент внесения и устройство приходят заголовками записи (`x-client-time`,
 * `x-device-id`) — теми же, что у любой другой операции. Дублировать их в теле
 * значит завести второй источник правды о времени.
 */
export const CreateToothEventRequest = z.object({
  kind: ToothEventKind,
  detail: ToothEventDetail.optional(),
  surfaces: z.array(Surface).max(6).optional(),
  icd10Code: z.string().max(10).optional(),
  notes: z.string().max(2000).optional(),
  /** Дата вмешательства. Без неё берётся момент внесения записи. */
  performedAt: z.iso.datetime().optional(),
});
export type CreateToothEventRequest = z.infer<typeof CreateToothEventRequest>;

/**
 * Исправление. Отдельный путь, а не поле в создании: исправление всегда
 * относится к конкретной существующей записи, и сервер обязан это проверить.
 * Указать `supersedesId` на чужой или уже исправленной записи нельзя.
 */
export const CorrectToothEventRequest = CreateToothEventRequest.extend({
  /** Зачем исправили. Видно в ленте рядом с зачёркнутой записью. */
  reason: z.string().min(1).max(500),
});
export type CorrectToothEventRequest = z.infer<typeof CorrectToothEventRequest>;

/** Состояние одного зуба для отрисовки формулы. */
export const ToothView = z.object({
  fdi: Fdi,
  state: ToothState,
  /** Поверхности, по которым есть незакрытые записи: их формула помечает. */
  surfaces: z.array(Surface).default([]),
  /** Когда по зубу вносили запись в последний раз. */
  lastEventAt: z.iso.datetime().nullable(),
});
export type ToothView = z.infer<typeof ToothView>;

export const DentalChart = z.object({
  patientId: Id,
  teeth: z.array(ToothView),
});
export type DentalChart = z.infer<typeof DentalChart>;

export const ToothPassport = z.object({
  patientId: Id,
  fdi: Fdi,
  state: ToothState,
  /** Хронология: от свежего к старому, вместе с исправленными записями. */
  events: z.array(ToothEventView),
});
export type ToothPassport = z.infer<typeof ToothPassport>;

/**
 * Все записи формулы по карте — для ленты событий пациента (веха 3.3).
 *
 * Запрос к серверу, а не чтение с устройства, по той же причине, что и
 * паспорт зуба: с вехи 1.6 устройство хранит снимок формулы, а не журнал
 * записей. Лента без связи показывает записи этого устройства, ещё не
 * отправленные, а полную историю — при подключении.
 */
export const PatientToothEvents = z.object({
  patientId: Id,
  /** От свежего к старому, вместе с исправленными записями. */
  events: z.array(ToothEventView),
});
export type PatientToothEvents = z.infer<typeof PatientToothEvents>;
